import { createContext, useContext } from 'react';
import { AuthState, LoginResult } from '@rsmaids/shared/types/auth';
import { UserInfo } from '@rsmaids/shared/types/auth';
import type { Notification } from '../hooks/useNotifications';

export interface CategoryCounts {
  booking: number;
  payment: number;
  team: number;
  form: number;
  overdue: number;
  autoInvoice: number;
}

export interface AuthContextType {
  user: UserInfo | null; // This is the current authenticated user
  notifications: Notification[];
  /** Notifications that arrived since the alerts were last opened (the badge number). */
  unreadCount: number;
  /** Resets `unreadCount` to 0 — call when the user opens the alerts. */
  markNotificationsSeen: () => void;
  categoryCounts: CategoryCounts;
  authState: {
    isLoading: boolean;
    isLoggedIn: boolean;
    user: UserInfo | null; // This is in the state object
  error: string | import('@rsmaids/shared/types/errors').ApiError | null;
  };
  actions: {
    login: (idToken: string, password?: string) => Promise<LoginResult>;
    loginBackend: () => Promise<LoginResult>;
    registerBackend: (email: string, password: string, firstName?: string, lastName?: string, phone?: string) => Promise<LoginResult>;
    resendVerification: (email: string) => Promise<LoginResult>;
    verifyEmailCode: (email: string, code: string) => Promise<LoginResult>;
    verifyEmployeeCode: (email: string, code: string) => Promise<LoginResult>;
    logout: () => Promise<void>;
    updateUserWithStoredProfile: () => Promise<void>;
    refreshUserData: () => Promise<void>;
    updateLocalPreferences: (preferences: { dashboardShortcuts?: string[] }) => void;
  };
}

// Create a default context value
export const defaultAuthState: AuthState = {
  isLoggedIn: false,
  isLoading: true,
  user: null,
  error: null
};

// Create the context with a default value
export const AuthContext = createContext<AuthContextType>({
  user: null,
  notifications: [],
  unreadCount: 0,
  markNotificationsSeen: () => {},
  categoryCounts: { booking: 0, payment: 0, team: 0, form: 0, overdue: 0, autoInvoice: 0 },
  authState: {
    isLoggedIn: false,
    isLoading: true,
    user: null,
    error: null
  },
  actions: {
  login: async () => ({ success: false, userID: '', error: 'Auth context not initialized' }),
  loginBackend: async () => ({ success: false, userID: '', error: 'Auth context not initialized' }),
  registerBackend: async () => ({ success: false, userID: '', error: 'Auth context not initialized' }),
  resendVerification: async () => ({ success: false, userID: '', error: 'Auth context not initialized' }),
  verifyEmailCode: async () => ({ success: false, userID: '', error: 'Auth context not initialized' }),
  verifyEmployeeCode: async () => ({ success: false, userID: '', error: 'Auth context not initialized' }),
    logout: async () => {},
    updateUserWithStoredProfile: async () => {},
    refreshUserData: async () => {},
    updateLocalPreferences: () => {}
  }
});

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);